import { Component, Input, Output, EventEmitter, forwardRef, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ICheckboxOption } from './ICheckboxOption';
import { Yii } from 'libraries/interfaces/Yii';
import { ILabels } from 'libraries/interfaces/ILabels';

@Component({
    selector: 'multi-checkbox-field',
    template: `
        <ul *ngIf="!isRadio">
            <li *ngFor="let option of options; let i = index; trackBy: getOptionTracking">
                <mat-checkbox
                        [value]="option.value"
                        [checked]="isChecked(option.value)"
                        [disabled]="disabled"
                        [matTooltip]="option.tooltip"
                        (change)="onCheckChange($event)">
                    <ng-container *ngIf="showLabel">{{ option.label }}</ng-container>
                </mat-checkbox>
            </li>
        </ul>
        <mat-radio-group *ngIf="isRadio" class="col" [value]="preSelectedValue?.length ? preSelectedValue[0]: null">
            <mat-radio-button *ngFor="let option of options; let i = index; trackBy: getOptionTracking"
                              [value]="option.value"
                              (change)="writeValue([option.value])">
                {{option.label}}
            </mat-radio-button>
        </mat-radio-group>
        <ng-container *ngIf="showLabel && allowSelectAllBehaviour && !isRadio">
            <btn *ngIf="showSelectAll()"
                 type="actionInlineLink"
                 [label]="labels.selectAll"
                 (onClick)="selectAll()"></btn>

            <btn *ngIf="showUnselectAll()"
                 type="actionInlineLink"
                 [label]="labels.unselectAll"
                 (onClick)="unselectAll()"></btn>
        </ng-container>


    `,
    styles: [`
        ul {
            list-style-type: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        }

        li {
            flex-grow: 0;
            margin-right: .75rem;
        }

        mat-checkbox ::ng-deep input {
            all: unset;
        }

        mat-checkbox.mat-checkbox-disabled {
            opacity: 50%
        }

        .col {
            display: flex;
            flex-direction: column;
        }
    `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiCheckboxFieldComponent),
            multi: true,
        }
    ]
})
export class MultiCheckboxFieldComponent implements ControlValueAccessor {

    @Input()
    public options: ICheckboxOption[];
    @Input()
    public selectAllWhenEmpty: boolean = false;
    @Input()
    public allowSelectAllBehaviour: boolean = true;
    @Input()
    public showLabel: boolean = true;
    @Input()
    public isRadio: boolean = false;
    @Input()
    public preSelectedValue: string[];

    @Output()
    public readonly onChange: EventEmitter<string[]> = new EventEmitter();

    public disabled: boolean = false;
    public labels: ILabels = {
        selectAll: Yii.t("wui-iqba-js", "Tout sélectionner"),
        unselectAll: Yii.t("wui-iqba-js", "Tout désélectionner"),
    };

    private touched: boolean = false;

    private selectedValues: Set<string> = new Set();
    private onValueChange: (value: string[]) => void;
    private onComponentTouched: () => void;
    private value: string[];

    public isChecked(value: string): boolean {

        return this.selectedValues.has(value);
    }

    public onCheckChange(event: MatCheckboxChange): void {
        if (!this.touched && this.onComponentTouched) {
            this.touched = true;
            this.onComponentTouched();
        }

        const previousValues: string[] = Array.from(this.selectedValues.values());

        if (event.checked) {
            this.writeValue(previousValues.concat([event.source.value]));
        } else {
            this.writeValue(previousValues.filter((value: string) => value !== event.source.value));
        }
    }

    public selectAll(): void {
        this.writeValue(values(this.options));
    }

    public unselectAll(): void {
        this.writeValue([]);
    }

    public showUnselectAll(): boolean {
        return this.options && this.options.length > 1 && this.options.length === this.selectedValues.size;
    }

    public showSelectAll(): boolean {
        return this.options && this.options.length > 1 && this.options.length !== this.selectedValues.size;
    }

    public writeValue(selectedValues: string[]): void {
        const nextSelectedValues: string[] = this.selectAllWhenEmpty && (!selectedValues || !selectedValues.length)
            ? values(this.options)
            : selectedValues || [];

        if (!areSame(selectedValues, this.selectedValues)) {
            this.selectedValues.clear();
            this.value = [];

            if (selectedValues) {
                selectedValues.forEach((value: string) => this.selectedValues.add(value));
            }

            this.value = [...nextSelectedValues];

            if (this.onValueChange) {
                this.onValueChange(this.value);
            }

            this.onChange.emit(this.value);
        }
    }

    //// Interface ControlValueAccessor
    public registerOnChange(fn: (value: string[]) => void): void {
        this.onValueChange = fn;
    }

    //// Interface ControlValueAccessor
    public registerOnTouched(fn: () => void): void {
        this.onComponentTouched = fn;
    }

    //// Interface ControlValueAccessor
    public setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public getOptionTracking(index: number, option: ICheckboxOption): string {
        return option.value;
    }
}

function areSame(newValues: string[], selectedValues: Set<string>): boolean {
    return !newValues && selectedValues.size === 0
        || newValues && newValues.length === selectedValues.size
        && newValues.every((value: string) => selectedValues.has(value));
}

function values(options: ICheckboxOption[]): string[] {
    return (options || []).map((option: ICheckboxOption): string => option.value);
}
